import { Fragment, h } from "preact";
import { useState } from "preact/hooks";
import { useDispatch } from "react-redux";

import talentsApi from "../api/talents.js";
import i18n from "../i18n.js";
import { setGlobalError, updateTalentStateState } from "../store/";

import Button from "./Button.jsx";
import CircleLoadingIndicator from "./CircleLoadingIndicator.jsx";
import MoreItem from "./MoreItem.jsx";
import Tooltip from "./Tooltip.jsx";

async function timeout(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export default function TalentAction({
  onChange,
  onClick,
  small,
  talent,
  text,
}) {
  const [sending, setSending] = useState(false);
  const dispatch = useDispatch();

  async function handleSendRequest(event) {
    event.stopPropagation();
    if (onClick) {
      return onClick(event);
    }
    event.preventDefault();
    setSending(true);
    try {
      const change = await talentsApi.changeState(talent.id, "requested");
      await timeout(200);
      dispatch(updateTalentStateState(change));
      onChange && onChange(change);
    } catch (err) {
      // Nothing to do?
    }
    setSending(false);
  }

  async function changeState(state) {
    if (talent.state === state) {
      return;
    }
    setSending(true);
    try {
      const change = await talentsApi.changeState(talent.id, state);
      dispatch(updateTalentStateState(change));
      onChange && onChange(change);
    } catch (err) {
      dispatch(setGlobalError(err.error));
    }
    setSending(false);
  }

  function stopPropagation(event) {
    event.stopPropagation();
  }

  switch (talent.state) {
    case "not_interacted":
      return (
        <Button
          text={text}
          small={small}
          disabled={sending}
          secondary={!text}
          onClick={handleSendRequest}
          data-tk-click="talent.request"
          iconFront={sending ? <CircleLoadingIndicator dark /> : null}
        >
          {i18n.t("component.talent_action.action.not_interacted")}
        </Button>
      );
    case "requested":
      return (
        <Fragment>
          <span
            data-tooltip-id={
              talent.stateRequestedBy && `talent-action-${talent.id}`
            }
          >
            <Button text={text} small={small} disabled secondary={!text}>
              {i18n.t("component.talent_action.action.requested")}
            </Button>
          </span>
          {talent.stateRequestedBy && (
            <Tooltip
              className="nioomi-tooltip"
              id={`talent-action-${talent.id}`}
            >
              {i18n.t("component.talent_action.action.requested_tooltip", {
                name: talent.stateRequestedBy,
              })}
            </Tooltip>
          )}
        </Fragment>
      );
    case "request_declined":
      return (
        <Button text={text} small={small} disabled secondary={!text}>
          {i18n.t("component.talent_action.action.request_declined")}
        </Button>
      );
    case "contacted":
    case "replied":
    case "screened":
    case "declined":
    case "hired":
    case "request_accepted": {
      return (
        <Button
          text={text}
          small={small}
          href={`mailto:${talent.data.email}`}
          onClick={stopPropagation}
          secondary={!text}
          disabled={sending}
        >
          {i18n.t("component.talent_action.action.request_accepted")}
        </Button>
      );
    }
    default:
      return null;
  }
}
